import { AlertCircle, Star } from 'lucide-react';
import { TableRow, compareTables, contraintesDeTable } from './terrainTypes';

const listeTables = (nums: string[]) => nums.length <= 1 ? nums.join('') : `${nums.slice(0, -1).join(', ')} et ${nums[nums.length - 1]}`;

/**
 * Plan de table (maquette 18d) — lecture seule, sur téléphone, pendant le service. Les allergies avant
 * les tables : un bandeau rouge nomme les tables concernées dès l'ouverture, et ces tables remontent en
 * tête de liste. Une allergie se lit AVEC un nom (« CAZAUX Lucie — sans gluten ») : c'est le nom qu'on
 * annonce au passe. Le mode terrain n'écrit rien ici ; la contrainte vient de la réponse du convive, le
 * numéro de table de son scan à l'arrivée.
 *
 * Texte à 12,5 px minimum, cibles de 44 px — l'écran doit rester lisible dans une salle sombre.
 */
export function TerrainPlanDeTable({ tables, seulementAllergies }: { tables: TableRow[]; seulementAllergies?: boolean }) {
    const avec = tables.map((t) => ({ t, c: contraintesDeTable(t) }));
    const concernees = avec.filter((x) => x.c.length > 0).sort((a, b) => compareTables(a.t.table_number, b.t.table_number));
    const sans = avec.filter((x) => x.c.length === 0).sort((a, b) => compareTables(a.t.table_number, b.t.table_number));
    const nbContraintes = concernees.reduce((n, x) => n + x.c.length, 0);
    const visibles = seulementAllergies ? concernees : [...concernees, ...sans];

    if (tables.length === 0) {
        return (
            <div className="p-[13px] rounded-[11px] bg-surface-soft border border-dashed border-border text-center">
                <p className="text-[12.5px] text-content-muted">Aucun convive ne s'est encore déclaré assis à une table.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5">
            {nbContraintes > 0 ? (
                <div className="flex items-center gap-2.5 px-[13px] py-3 rounded-[11px] bg-state-danger-soft border border-state-danger/30">
                    <span className="w-[30px] h-[30px] shrink-0 rounded-lg bg-surface border border-state-danger/30 text-state-danger flex items-center justify-center"><AlertCircle size={15} /></span>
                    <div className="min-w-0">
                        <div className="text-[13px] font-bold text-state-danger">{nbContraintes} contrainte{nbContraintes > 1 ? 's' : ''} sur {concernees.length} table{concernees.length > 1 ? 's' : ''}</div>
                        <div className="text-[12.5px] text-state-danger">Table{concernees.length > 1 ? 's' : ''} {listeTables(concernees.map((x) => x.t.table_number))} — à annoncer au passe.</div>
                    </div>
                </div>
            ) : (
                <div className="px-[13px] py-3 rounded-[11px] bg-state-ok-soft border border-state-ok/30 text-[12.5px] font-semibold text-state-ok">Aucune allergie déclarée sur les tables installées.</div>
            )}

            {visibles.map(({ t, c }) => {
                const has = c.length > 0;
                return (
                    <div key={t.table_number} className={`rounded-[11px] bg-surface border overflow-hidden ${has ? 'border-state-danger/30' : 'border-border-soft'}`}>
                        <div className={`flex items-center gap-[11px] px-[13px] py-[11px] min-h-[56px] ${has ? 'bg-surface' : 'bg-surface-soft'}`}>
                            <span className={`w-[34px] h-[34px] shrink-0 rounded-[9px] border flex items-center justify-center text-[14px] font-bold tabular-nums ${
                                has ? 'bg-state-danger-soft border-state-danger/30 text-state-danger' : 'bg-surface-soft border-border text-content-muted'}`}>{t.table_number}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-bold text-content truncate flex items-center gap-1.5">
                                    Table {t.table_number}
                                    {t.is_vip && <Star size={13} className="text-state-warn shrink-0" fill="currentColor" />}
                                </div>
                                <div className="mt-px text-[12.5px] text-content-muted tabular-nums">{t.guests.length} couvert{t.guests.length > 1 ? 's' : ''} installé{t.guests.length > 1 ? 's' : ''}</div>
                            </div>
                            <span className={`inline-flex items-center h-6 px-2.5 rounded-full border text-[11px] font-bold whitespace-nowrap shrink-0 ${
                                has ? 'bg-state-danger-soft border-state-danger/30 text-state-danger' : 'bg-state-ok-soft border-state-ok/30 text-state-ok'}`}>
                                {has ? `${c.length} ${c.length > 1 ? 'contraintes' : 'contrainte'}` : 'Aucune allergie'}
                            </span>
                        </div>
                        {has && (
                            <div className="px-[13px] py-[11px] border-t border-state-danger/30 flex flex-col gap-[7px]">
                                {c.map((a, i) => (
                                    <div key={i} className="flex items-baseline gap-[9px]">
                                        <span className={`w-[5px] h-[5px] shrink-0 mt-1.5 rounded-full ${a.allergie ? 'bg-state-danger' : 'bg-state-warn'}`} />
                                        <div className="min-w-0 text-[12.5px]">
                                            <span className="font-bold text-content">{a.who}</span>
                                            <span className={a.allergie ? 'text-state-danger' : 'text-state-warn'}> — {a.what}{a.allergie ? '' : a.medicale ? ' (note médicale)' : ' (restriction)'}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {seulementAllergies && sans.length > 0 && (
                <div className="p-[13px] rounded-[11px] bg-surface-soft border border-dashed border-border text-center">
                    <p className="text-[12.5px] text-content-muted">{sans.length} autre{sans.length > 1 ? 's' : ''} table{sans.length > 1 ? 's' : ''} sans allergie déclarée.</p>
                </div>
            )}
        </div>
    );
}
